/**
 * Shared helpers for the message editors (post-booking message + reminders):
 * a closed set of {{placeholders}}, click-to-insert buttons, a meeting-type
 * switch, and live email/SMS previews. Keeps the two editors consistent.
 */
export type PreviewMeetingType = 'in_person' | 'video_call' | 'phone_call';

// Mirrors ALLOWED_TOKENS in src/lib/notify/confirmation-message.ts.
export const MESSAGE_TOKENS: { token: string; label: string }[] = [
  { token: 'bookee_name', label: 'Customer name' },
  { token: 'start_time', label: 'Start time' },
  { token: 'staff_name', label: 'Staff name' },
  { token: 'service_name', label: 'Service' },
  { token: 'location_name', label: 'Location' },
  { token: 'location_address', label: 'Address' },
  { token: 'meeting_details', label: 'Meeting details' },
];

const PREVIEW_SAMPLE = {
  bookee_name: 'Jordan Smith',
  start_time: 'Mon, Jun 30 at 9:30 AM',
  location_name: 'Downtown Office',
  location_address: '123 Main St, Regina',
  staff_name: 'Alex Lee',
  service_name: 'Consultation',
  meeting_link: 'https://meet.example.com/abc-defg-hij',
};

function meetingDetailsPreview(t: PreviewMeetingType): string {
  const when = PREVIEW_SAMPLE.start_time;
  if (t === 'video_call') return `Your video meeting at ${when} using this link: ${PREVIEW_SAMPLE.meeting_link}`;
  if (t === 'phone_call') return `Your phone call at ${when}.`;
  return `Your in-person meeting at ${when} at ${PREVIEW_SAMPLE.location_address}`;
}

export function renderMessagePreview(message: string, t: PreviewMeetingType): string {
  const values: Record<string, string> = {
    bookee_name: PREVIEW_SAMPLE.bookee_name,
    start_time: PREVIEW_SAMPLE.start_time,
    location_name: PREVIEW_SAMPLE.location_name,
    location_address: PREVIEW_SAMPLE.location_address,
    staff_name: PREVIEW_SAMPLE.staff_name,
    service_name: PREVIEW_SAMPLE.service_name,
    meeting_details: meetingDetailsPreview(t),
  };
  return message.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (full, name: string) =>
    name in values ? values[name]! : full,
  );
}

/** Insert `{{token}}` at the field's caret. Returns the new value + caret so the
 * caller can set state and restore the cursor. */
export function insertTokenAt(
  el: HTMLTextAreaElement | HTMLInputElement | null,
  current: string,
  token: string,
): { next: string; caret: number } {
  const snippet = `{{${token}}}`;
  const start = el?.selectionStart ?? current.length;
  const end = el?.selectionEnd ?? current.length;
  const next = current.slice(0, start) + snippet + current.slice(end);
  return { next, caret: start + snippet.length };
}

/** Row of click-to-insert placeholder buttons. */
export function PlaceholderButtons({ onInsert }: { onInsert: (token: string) => void }) {
  return (
    <div className="token-btns">
      <span className="muted small">Insert:</span>
      {MESSAGE_TOKENS.map((t) => (
        <button key={t.token} type="button" className="token-btn" onClick={() => onInsert(t.token)}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** "Preview as" meeting-type switch (drives {{meeting_details}} in previews). */
export function MeetingTypeSeg({
  value,
  onChange,
}: {
  value: PreviewMeetingType;
  onChange: (t: PreviewMeetingType) => void;
}) {
  return (
    <div className="msg-preview-head" style={{ marginBottom: '0.75rem' }}>
      <span className="muted small">Preview as</span>
      <div className="seg">
        {(
          [
            ['in_person', 'In person'],
            ['video_call', 'Video'],
            ['phone_call', 'Phone'],
          ] as [PreviewMeetingType, string][]
        ).map(([v, label]) => (
          <button key={v} className={value === v ? 'seg-on' : ''} onClick={() => onChange(v)}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

// --- Default reminder copy (mirrors bookingReminder in templates.ts) --------
// Shown in the preview when a reminder field is blank, so the admin sees the
// actual wording that will go out rather than a "uses the default" note.

// Human lead-time phrase — mirrors leadPhrase() in src/lib/notify/templates.ts.
function leadPhrase(offsetMinutes: number): string {
  if (offsetMinutes >= 1440 && offsetMinutes % 1440 === 0) {
    const days = offsetMinutes / 1440;
    return days === 1 ? 'tomorrow' : `in ${days} days`;
  }
  if (offsetMinutes >= 60 && offsetMinutes % 60 === 0) {
    const hours = offsetMinutes / 60;
    return hours === 1 ? 'in 1 hour' : `in ${hours} hours`;
  }
  if (offsetMinutes >= 60) return 'soon';
  return `in ${offsetMinutes} minutes`;
}

function reminderWhere(t: PreviewMeetingType): string {
  return t === 'video_call'
    ? PREVIEW_SAMPLE.meeting_link
    : `${PREVIEW_SAMPLE.location_name} — ${PREVIEW_SAMPLE.location_address}`;
}

export function defaultReminderEmailBody(offsetMinutes: number, t: PreviewMeetingType): string {
  const lead = leadPhrase(offsetMinutes);
  return [
    `Hi ${PREVIEW_SAMPLE.bookee_name},`,
    ``,
    `Just a reminder of your upcoming appointment ${lead}:`,
    ``,
    `What: ${PREVIEW_SAMPLE.service_name} with ${PREVIEW_SAMPLE.staff_name}`,
    `When: ${PREVIEW_SAMPLE.start_time}`,
    `Where: ${reminderWhere(t)}`,
  ].join('\n');
}

export function defaultReminderSmsText(offsetMinutes: number, brandName: string): string {
  const lead = leadPhrase(offsetMinutes);
  return `${brandName}: reminder — ${PREVIEW_SAMPLE.service_name} with ${PREVIEW_SAMPLE.staff_name} ${lead} (${PREVIEW_SAMPLE.start_time}). Reply STOP to opt out.`;
}

/** Email message preview inside a mini branded shell (logo + signature + footer).
 * When `body` is blank and a `fallbackBody` is given, the default wording is
 * shown (flagged) so the admin sees exactly what goes out. */
export function EmailMessagePreview({
  brandName,
  brandLogoUrl,
  body,
  previewType,
  fallbackBody,
}: {
  brandName: string;
  brandLogoUrl: string | null;
  body: string;
  previewType: PreviewMeetingType;
  fallbackBody?: string;
}) {
  const usingDefault = !body.trim() && !!fallbackBody;
  const content = body.trim() ? body : fallbackBody ?? '';
  return (
    <div className="email-preview">
      <div className="email-preview-brand">
        {brandLogoUrl ? <img src={brandLogoUrl} alt={brandName} /> : <strong>{brandName}</strong>}
      </div>
      <div className="email-preview-body">
        {usingDefault && <div className="muted small preview-default-tag">Default wording</div>}
        {renderMessagePreview(content, previewType)}
        <div className="email-preview-sig">— {brandName}</div>
      </div>
      <div className="email-preview-foot">Sent with Invitey · appointment scheduling</div>
    </div>
  );
}

/** SMS message preview (plain). Shows `fallbackText` (the default) when blank. */
export function SmsMessagePreview({
  text,
  previewType,
  fallbackText,
}: {
  text: string;
  previewType: PreviewMeetingType;
  fallbackText?: string;
}) {
  const usingDefault = !text.trim() && !!fallbackText;
  const content = text.trim() ? text : fallbackText ?? '';
  return (
    <div className="msg-preview">
      <div className="msg-preview-head">
        <span className="muted small">Text preview{usingDefault ? ' · default' : ''}</span>
      </div>
      <div className="msg-preview-body">{renderMessagePreview(content, previewType)}</div>
    </div>
  );
}
